import { kUserSettingsApiPath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { UserSettingsPatchResponse, UserSettingsPatchResponseSchema, UserSettingsPatchSchema } from '@/settings/schemas/settings-schema.ts'
import { z } from 'zod'

import { kErrorCodeAuthError } from '@/constants/constants-error-codes'

import { getAuth } from 'firebase/auth'

import * as Sentry from '@sentry/browser'
import { nanoid } from 'nanoid'

/**
 * Update User Settings via PATCH Request
 * @returns
 */
export type UserSettingsPatchPayload = z.infer<typeof UserSettingsPatchSchema>

export default async function patchUserSettings(userSettings: UserSettingsPatchPayload): Promise<UserSettingsPatchResponse> {
  const sentry_transaction_id = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw Error(kErrorCodeAuthError)

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'PATCH',
      body: JSON.stringify(userSettings),
    }

    const apiUrl = kPaxtonAppApiRESTBaseUrl() + kUserSettingsApiPath
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentry_transaction_id })

        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions: requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new Error(`Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    // Validate the response data using Zod
    const parsedData = UserSettingsPatchResponseSchema.parse(data)
    return parsedData
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })

      Sentry.captureException(e, {
        extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
      })
    })
    throw e
  }
}
